import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  CHART_NOTES_STYLE_TAB_LIST_SX,
  ChartNotesStyleTabLabel
} from "components/chart-notes-style-tabs";
import { selectCurrentUser } from "features/auth/auth-slice";
import {
  AMBIENT_SCRIBE_STATUS_COMPLETED,
  AMBIENT_SCRIBE_STATUS_PAUSED,
  AMBIENT_SCRIBE_STATUS_PROCESSING,
  AMBIENT_SCRIBE_STATUS_RECORDING
} from "features/medical-scribe/medical-scribe-constants";
import {
  DEFAULT_AMBIENT_SCRIBE_QUERY_PAUSED,
  useGetAmbientScribeQuery,
  useGetAmbientScribeTemplateQuery,
  useUpdateAmbientScribeMutation
} from "services/ambient-scribe";
import {
  APPOINTMENT_TYPES_QUERY_SCRIBE_TEMPLATE_LABEL,
  useGetAppointmentTypesQuery
} from "services/appointment-types";
import type { AppointmentType } from "services/types/appointment-types.type";

import EditScribeMetadataHeader from "./edit-scribe-metadata-header.component";
import PausedRecordingSessionsInfoBanner from "./paused-recording-sessions-info-banner.component";
import PausedScribeActionButtons from "./paused-scribe-action-buttons.component";
import PausedStatusWarningAlert from "./paused-status-warning-alert.component";
import RecordingSessionsTable from "./recording-sessions-table.component";
import ScribeContextEditor from "./scribe-context-editor.component";
import { usePausedScribeActions } from "./use-paused-scribe-actions.hook";

type Props = {
  scribeId: number | null;
  onClose: () => void;
  onFinalized: () => void;
  initialBottomTab?: "sessions" | "context";
};

type BottomTab = "sessions" | "context";

const resolveBottomTab = (tab: BottomTab | undefined): BottomTab =>
  tab === "context" ? "context" : "sessions";

export default function EditScribeTab({
  scribeId,
  onClose,
  onFinalized,
  initialBottomTab = "sessions"
}: Props) {
  const dispatch = useAppDispatch();

  const resolvedInitialTab = resolveBottomTab(initialBottomTab);

  const [bottomTab, setBottomTab] = useState<BottomTab>(resolvedInitialTab);

  useEffect(() => {
    setBottomTab(resolveBottomTab(initialBottomTab));
  }, [scribeId, initialBottomTab]);
  const [addToChartNotes, setAddToChartNotes] = useState(false);
  const hasUserToggledAddToChartRef = useRef(false);
  const flushContextSaveRef = useRef<(() => Promise<void>) | null>(null);
  const [isAudioDeleteInFlight, setIsAudioDeleteInFlight] = useState(false);

  const { data: scribe, isLoading } = useGetAmbientScribeQuery(
    { id: scribeId!, query: DEFAULT_AMBIENT_SCRIBE_QUERY_PAUSED },
    { skip: !scribeId }
  );

  const templateIdForQuery = scribe?.ambient_scribe_template_id ?? null;
  const { data: scribeTemplate } = useGetAmbientScribeTemplateQuery(
    Number(templateIdForQuery),
    { skip: templateIdForQuery == null }
  );

  const matchedScribeTemplate = useMemo(() => {
    const id = scribe?.ambient_scribe_template_id ?? null;
    if (id == null) return null;
    if (scribeTemplate != null && Number(scribeTemplate.id) === Number(id)) {
      return scribeTemplate;
    }
    return null;
  }, [scribe?.ambient_scribe_template_id, scribeTemplate]);

  // Narrow projection: this row needs a name, a duration, and the link column — nothing else.
  // The previous `{}` fetched the entire unqueried appointment-type payload.
  const { data: appointmentTypes = [] } = useGetAppointmentTypesQuery({
    query: APPOINTMENT_TYPES_QUERY_SCRIBE_TEMPLATE_LABEL
  });

  const currentUser = useAppSelector(selectCurrentUser);

  const [updateScribe] = useUpdateAmbientScribeMutation();

  const isPaused = scribe?.status === AMBIENT_SCRIBE_STATUS_PAUSED;
  const isCompleted = scribe?.status === AMBIENT_SCRIBE_STATUS_COMPLETED;
  const isRecordingStatus = scribe?.status === AMBIENT_SCRIBE_STATUS_RECORDING;
  const isProcessingStatus =
    scribe?.status === AMBIENT_SCRIBE_STATUS_PROCESSING;

  useEffect(() => {
    if (scribe?.id) {
      hasUserToggledAddToChartRef.current = false;
      setAddToChartNotes(
        scribe.add_to_chart_notes ??
          matchedScribeTemplate?.add_to_chart_notes ??
          false
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scribe?.id]);

  useEffect(() => {
    if (
      scribe?.id &&
      !hasUserToggledAddToChartRef.current &&
      scribe.add_to_chart_notes == null &&
      matchedScribeTemplate?.add_to_chart_notes != null
    ) {
      setAddToChartNotes(matchedScribeTemplate.add_to_chart_notes);
    }
  }, [
    scribe?.id,
    scribe?.add_to_chart_notes,
    matchedScribeTemplate?.add_to_chart_notes
  ]);

  const showPatchError = useCallback(
    (fallback: string) => {
      dispatch(setCustomMessage(fallback));
      dispatch(setOpen(4000));
    },
    [dispatch]
  );

  const handleAddToChartNotesChange = useCallback(
    async (value: boolean) => {
      if (!scribeId) return;
      hasUserToggledAddToChartRef.current = true;
      setAddToChartNotes(value);
      try {
        await updateScribe({
          id: scribeId,
          add_to_chart_notes: value
        }).unwrap();
      } catch {
        setAddToChartNotes((prev) => !value);
        showPatchError(
          "Failed to update Add to Chart Note setting. Please try again."
        );
      }
    },
    [scribeId, updateScribe, showPatchError]
  );

  const scribeTemplateLabel = useMemo(() => {
    const id = scribe?.ambient_scribe_template_id ?? null;
    if (id == null) return "auto";
    return matchedScribeTemplate?.name ?? "—";
  }, [scribe?.ambient_scribe_template_id, matchedScribeTemplate?.name]);

  // Read from the AUTHORITATIVE direction (`AppointmentType.ambient_scribe_template_id`). The
  // template's own `appointment_type_id` column was removed backend-side, so the previous
  // implementation silently rendered "—" for every scribe. A template can serve several
  // appointment types now, so join them rather than picking one.
  const appointmentTypeLabel = useMemo(() => {
    const templateId = matchedScribeTemplate?.id;
    if (templateId == null) return "—";
    const linked = appointmentTypes.filter(
      (t: AppointmentType) =>
        Number(t.ambient_scribe_template_id) === Number(templateId)
    );
    if (linked.length === 0) return "—";
    return linked
      .map((t: AppointmentType) =>
        t.default_duration ? `${t.name} (${t.default_duration})` : t.name
      )
      .join(", ");
  }, [matchedScribeTemplate?.id, appointmentTypes]);

  const {
    handleResume,
    handleComplete,
    isResuming,
    isResumePleaseWait,
    isCompleting,
    isWidgetActive,
    isLocallyPausedWidget,
    canCompleteFromFailedOrCancelled,
    isFailedOrCancelledAwaitingChunks
  } = usePausedScribeActions({
    scribeId,
    scribeStatus: scribe?.status,
    patient: scribe?.patient,
    chartNoteId: scribe?.chart_note?.id,
    onClose,
    onFinalized,
    onBeforeAction: () => flushContextSaveRef.current?.() ?? Promise.resolve()
  });

  if (!scribeId) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "text.secondary"
        }}
      >
        <Typography variant="body2">
          Select a paused scribe from the list to edit
        </Typography>
      </Box>
    );
  }

  if (isLoading || !scribe) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%"
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden"
      }}
    >
      <EditScribeMetadataHeader
        createdAt={scribe.created_at}
        status={scribe.status}
        appointmentTypeLabel={appointmentTypeLabel}
        scribeTemplateLabel={scribeTemplateLabel}
        addToChartNotes={addToChartNotes}
        isPaused={isPaused}
        onAddToChartNotesChange={handleAddToChartNotesChange}
      />

      {isPaused && !isCompleting && <PausedStatusWarningAlert />}

      {!isPaused && (
        <PausedRecordingSessionsInfoBanner userId={currentUser?.id} />
      )}

      <Box sx={{ pt: 1, pb: 1, flexShrink: 0 }}>
        <PausedScribeActionButtons
          isPaused={isPaused}
          isCompleted={isCompleted}
          isRecordingStatus={isRecordingStatus}
          isProcessingStatus={isProcessingStatus}
          canCompleteFromFailedOrCancelled={canCompleteFromFailedOrCancelled}
          isFailedOrCancelledAwaitingChunks={isFailedOrCancelledAwaitingChunks}
          isWidgetActive={isWidgetActive}
          isLocallyPausedWidget={isLocallyPausedWidget}
          isResuming={isResuming || isAudioDeleteInFlight}
          isResumePleaseWait={isResumePleaseWait || isAudioDeleteInFlight}
          isCompleting={isCompleting || isAudioDeleteInFlight}
          onResume={handleResume}
          onComplete={handleComplete}
        />
      </Box>

      <TabContext value={bottomTab}>
        <TabList
          onChange={(_, v) => setBottomTab(v as BottomTab)}
          sx={{
            ...CHART_NOTES_STYLE_TAB_LIST_SX,
            mt: 2,
            flexShrink: 0
          }}
        >
          <Tab
            value="sessions"
            label={
              <ChartNotesStyleTabLabel
                label="Recording Sessions"
                isActive={bottomTab === "sessions"}
              />
            }
          />
          <Tab
            value="context"
            label={
              <ChartNotesStyleTabLabel
                label="Scribe Context"
                isActive={bottomTab === "context"}
              />
            }
          />
        </TabList>
      </TabContext>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}
      >
        {bottomTab === "sessions" && (
          <Box sx={{ pt: 1, overflow: "auto", flex: 1, minHeight: 0 }}>
            <RecordingSessionsTable
              scribeId={scribeId}
              scribeStatus={scribe.status}
              onDeleteInFlightChange={setIsAudioDeleteInFlight}
            />
          </Box>
        )}

        <Box
          sx={{
            display: bottomTab === "context" ? "flex" : "none",
            overflow: "hidden",
            flex: 1,
            flexDirection: "column",
            minHeight: 0
          }}
        >
          <ScribeContextEditor
            scribeId={scribeId}
            customContext={scribe.custom_context}
            hasLinkedChartNote={scribe.chart_note?.id != null}
            patientId={scribe.patient?.id}
            userId={currentUser?.id}
            isActive={bottomTab === "context"}
            onFlushSaveRef={flushContextSaveRef}
          />
        </Box>
      </Box>
    </Box>
  );
}
